import { useState, useEffect, useRef, Fragment } from "react";
import useInstanceInfo from "../hooks/useInstanceInfo";
import LegalLink from "./shared/LegalLink";

// "A" or "A and B", as the sentence reads with both documents configured.
const joinLinks = (links) => links.map((link, i) => (
  <Fragment key={i}>
    {i > 0 && ' and '}
    {link}
  </Fragment>
));

// `loading` from the parent means it is submitting; `instanceLoading` is the installation's
// settings (which legal documents are configured) still loading.
const VendorInviteTermsAcceptance = ({ onAccept, onDecline, inviteDetails, loading: submitting }) => {
  const { info, loading: instanceLoading, error: instanceError, retry } = useInstanceInfo();
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [ready, setReady] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const skippedRef = useRef(false);

  const legal = info.legal;
  const docs = [
    { url: legal.vendorInviteeTermsUrl, label: 'Vendor & Invitee Terms' },
    { url: legal.privacyPolicyUrl, label: 'Privacy Policy' },
  ].filter((doc) => doc.url);
  const loaded = !instanceLoading && !instanceError;
  const hasDocs = docs.length > 0;

  // 800ms after the documents render, fade hint + enable checkboxes + fill progress bar
  useEffect(() => {
    if (!loaded || !hasDocs) return undefined;
    const timer = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(timer);
  }, [loaded, hasDocs]);

  // Nothing to accept on this installation: continue exactly once. Only a successful load with no
  // documents skips; loading and a settings error never do (fail closed).
  useEffect(() => {
    if (loaded && !hasDocs && !skippedRef.current) {
      skippedRef.current = true;
      onAccept();
    }
  }, [loaded, hasDocs, onAccept]);

  const canAccept = check1 && check2 && !submitting;
  const orgName = inviteDetails?.organizationName || 'The requesting organisation';
  const assessmentName = inviteDetails?.assessmentName;
  const expiresIn = inviteDetails?.accessExpiresDays || 30;

  if (accepted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#fafafa] p-4">
        <div className="w-full max-w-[480px] bg-white rounded-2xl border border-[#e5e5e5] shadow-sm overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-11 h-11 rounded-full bg-[#E1F5EE] flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth={2.5}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="text-[16px] font-medium text-[#1d1d1f] mb-1">Assessment access granted</h2>
            <p className="text-[13px] text-[#6e6e73]">Your acceptance has been recorded. You can now begin the vendor assessment.</p>
          </div>
        </div>
      </div>
    );
  }

  const header = (
    <>
      {/* Header */}
      <div className="p-5 pb-4 border-b border-[#e5e5e5]">
        <div>
          <span className="logo-wordmark-text logo-wordmark-text-gradient block mb-2.5">AXARIS™</span>
          <div>
            <h1 className="text-[15px] font-medium text-[#1d1d1f]">Vendor risk assessment invitation</h1>
            <p className="text-[13px] text-[#6e6e73]">
              You have been invited to join {orgName} via Axaris TPRM
            </p>
            <span className="inline-block mt-1.5 text-[11px] font-medium px-2 py-0.5 rounded bg-[#E1F5EE] text-[#0F6E56]">
              Vendor invitee
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const declineButton = (
    <button
      onClick={onDecline}
      disabled={submitting}
      className="px-3 py-2.5 text-[13px] text-[#6e6e73] border border-[#d2d2d7] rounded-lg hover:bg-[#f5f5f7] transition-colors disabled:opacity-50"
    >
      Decline
    </button>
  );

  const submittingLabel = 'Setting up...';

  const card = (body) => (
    <div className="flex items-center justify-center min-h-screen bg-[#fafafa] p-4">
      <div className="w-full max-w-[480px] bg-white rounded-2xl border border-[#e5e5e5] shadow-sm overflow-hidden">
        {header}
        <div className="p-5">{body}</div>
      </div>
    </div>
  );

  if (instanceLoading) {
    return card(
      <>
        <p className="text-[13px] text-[#6e6e73] text-center mb-5">Loading…</p>
        <div className="flex gap-2">
          {declineButton}
          <button
            disabled
            className="flex-1 py-2.5 text-[13px] font-medium text-white bg-[#1D9E75] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            Accept and begin assessment
          </button>
        </div>
      </>
    );
  }

  if (instanceError) {
    return card(
      <>
        <p className="text-[13px] text-[#6e6e73] text-center mb-5">
          {"We couldn't load the terms for this installation."}
        </p>
        <div className="flex gap-2">
          {declineButton}
          <button
            onClick={retry}
            className="flex-1 py-2.5 text-[13px] font-medium text-white bg-[#1D9E75] rounded-lg hover:bg-[#0F6E56] transition-colors flex items-center justify-center gap-2"
          >
            Try again
          </button>
        </div>
      </>
    );
  }

  if (!hasDocs) {
    // Skipped. While the parent submits, show progress instead of a blank page.
    if (!submitting) return null;
    return card(<p className="text-[13px] text-[#6e6e73] text-center">{submittingLabel}</p>);
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#fafafa] p-4">
      <div className="w-full max-w-[480px] bg-white rounded-2xl border border-[#e5e5e5] shadow-sm overflow-hidden">
        {header}

        {/* Progress bar */}
        <div className="h-[2px] bg-[#e5e5e5] overflow-hidden">
          <div
            className="h-full bg-[#1D9E75] transition-all duration-200"
            style={{ width: ready ? '100%' : '0%' }}
          />
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Context box */}
          <div className="bg-[#f9fafb] border border-[#e5e5e5] rounded-lg p-3 mb-4 text-[13px] text-[#6e6e73] leading-relaxed">
            {assessmentName && (
              <><strong className="text-[#1d1d1f] font-medium">Assessment:</strong> {assessmentName}<br /></>
            )}
            <strong className="text-[#1d1d1f] font-medium">Requested by:</strong> {orgName}<br />
            <strong className="text-[#1d1d1f] font-medium">Access expires:</strong> {expiresIn} days from acceptance
          </div>

          {/* Hint text */}
          <p
            className="text-[12px] text-[#86868b] text-center mb-3 transition-opacity duration-300"
            style={{ opacity: ready ? 0 : 1 }}
          >
            Review the documents linked below before accepting
          </p>

          {/* Checkboxes */}
          <div className="flex flex-col gap-2.5 mb-5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={check1}
                onChange={(e) => setCheck1(e.target.checked)}
                disabled={!ready}
                className="w-[15px] h-[15px] mt-0.5 flex-shrink-0 accent-[#1D9E75]"
              />
              <span className="text-[13px] text-[#6e6e73] leading-relaxed">
                I have read and accept the{' '}
                {joinLinks(docs.map((doc) => (
                  <LegalLink key={doc.label} url={doc.url} newTab className="text-[#1D9E75] no-underline hover:underline">{doc.label}</LegalLink>
                )))}
              </span>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={check2}
                onChange={(e) => setCheck2(e.target.checked)}
                disabled={!ready}
                className="w-[15px] h-[15px] mt-0.5 flex-shrink-0 accent-[#1D9E75]"
              />
              <span className="text-[13px] text-[#6e6e73] leading-relaxed">
                I confirm I am authorised to submit information on behalf of my organisation,
                that all submissions will be accurate, and I agree to keep all assessment details
                and platform methodology confidential
              </span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            {declineButton}
            <button
              onClick={() => {
                setAccepted(true);
                onAccept();
              }}
              disabled={!canAccept}
              className="flex-1 py-2.5 text-[13px] font-medium text-white bg-[#1D9E75] rounded-lg hover:bg-[#0F6E56] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full border-2 border-transparent border-t-white border-r-white h-3.5 w-3.5" />
              )}
              {submitting ? submittingLabel : 'Accept and begin assessment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorInviteTermsAcceptance;
